import React, { useState, useEffect } from 'react';
import { Activity, Shield, AlertTriangle, Server } from 'lucide-react';
import socketService from '../../services/socketService';

const NetworkMonitor = ({ scenarioData }) => {
    const [traffic, setTraffic] = useState([]);
    const [stats, setStats] = useState({ total: 0, malicious: 0, safe: 0 });

    useEffect(() => {
        // If scenarioData is provided, use it instead of fetching from OpenAEV
        if (scenarioData && scenarioData.length > 0) {
            // Simulate real-time traffic arrival
            let index = 0;
            const interval = setInterval(() => {
                if (index < scenarioData.length) {
                    setTraffic(prev => [...prev, scenarioData[index]]);
                    index++;
                } else {
                    clearInterval(interval);
                }
            }, 200); // Add new packet every 200ms

            return () => clearInterval(interval);
        } else {
            // Fallback: fetch from OpenAEV if no scenario data
            const fetchData = async () => {
                try {
                    const response = await fetch('http://localhost:3001/api/openaev/payloads');
                    const data = await response.json();
                    updateTraffic(data);
                } catch (error) {
                    console.error('Failed to fetch network data', error);
                }
            };

            const updateTraffic = (data) => {
                const formattedTraffic = data.map(p => ({
                    id: p.payload_id,
                    source: p.network_traffic_ip_src || '192.168.1.1',
                    dest: p.network_traffic_ip_dst || '10.0.0.5',
                    protocol: p.network_traffic_protocol || 'TCP',
                    status: p.payload_status || 'unknown',
                    timestamp: new Date(p.payload_created_at).toLocaleTimeString()
                }));

                setTraffic(formattedTraffic);
                setStats({
                    total: formattedTraffic.length,
                    malicious: formattedTraffic.filter(t => t.status === 'malicious').length,
                    safe: formattedTraffic.filter(t => t.status !== 'malicious').length
                });
            };

            fetchData();

            // Socket Listener
            const socket = socketService.connect();
            socket.on('network_event', (newEvent) => {
                console.log('New network event:', newEvent);
                fetchData();
            });

            return () => {
                socketService.disconnect();
            };
        }
    }, [scenarioData]);

    // Update stats whenever traffic changes
    useEffect(() => {
        setStats({
            total: traffic.length,
            malicious: traffic.filter(t => t.status === 'malicious' || t.status === 'suspicious').length,
            safe: traffic.filter(t => t.status === 'safe').length
        });
    }, [traffic]);

    return (
        <div className="bg-gray-900 text-white p-6 rounded-lg shadow-lg border border-gray-700 w-full max-w-4xl">
            <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold flex items-center gap-2">
                    <Activity className="text-blue-400" /> Network Traffic Monitor
                </h2>
                <div className="flex gap-4 text-sm">
                    <span className="flex items-center gap-1 text-green-400"><Server size={16} /> Active: {stats.total}</span>
                    <span className="flex items-center gap-1 text-red-400"><AlertTriangle size={16} /> Threats: {stats.malicious}</span>
                </div>
            </div>

            {/* Traffic Visualization Bar */}
            <div className="h-32 bg-gray-800 rounded-md mb-6 flex items-end p-2 gap-1 overflow-hidden relative">
                {traffic.slice(0, 50).map((t, i) => (
                    <div
                        key={i}
                        className={`w-2 rounded-t ${t.status === 'malicious' ? 'bg-red-500' : 'bg-blue-500'}`}
                        style={{ height: `${Math.random() * 100}%` }} // Mock height for visualization
                        title={`${t.source} -> ${t.dest}`}
                    ></div>
                ))}
                <div className="absolute top-2 right-2 text-xs text-gray-500">Live Traffic</div>
            </div>

            {/* Recent Logs */}
            <div className="overflow-x-auto">
                <table className="w-full text-left text-sm text-gray-400">
                    <thead className="bg-gray-800 text-gray-200 uppercase">
                        <tr>
                            <th className="p-2">Time</th>
                            <th className="p-2">Source</th>
                            <th className="p-2">Destination</th>
                            <th className="p-2">Protocol</th>
                            <th className="p-2">Status</th>
                        </tr>
                    </thead>
                    <tbody>
                        {traffic.slice(0, 10).map((t) => (
                            <tr key={t.id} className="border-b border-gray-800 hover:bg-gray-800/50">
                                <td className="p-2">{t.timestamp || new Date(t.timestamp).toLocaleTimeString()}</td>
                                <td className="p-2 font-mono text-blue-300">{t.source}</td>
                                <td className="p-2 font-mono text-purple-300">{t.dest}</td>
                                <td className="p-2">{t.protocol}</td>
                                <td className="p-2">
                                    <span className={`px-2 py-0.5 rounded text-xs ${t.status === 'malicious' || t.status === 'suspicious'
                                        ? 'bg-red-900 text-red-200'
                                        : 'bg-green-900 text-green-200'
                                        }`}>
                                        {t.status}
                                    </span>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default NetworkMonitor;